import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router";
import "../styles/Header.css";

const Header: React.FC = () => {
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [isServicesOpen, setServicesOpen] = useState(false);
    const [isLinksOpen, setLinksOpen] = useState(false);
    const [isHomeownersOpen, setHomeownersOpen] = useState(false);
    const [isClientsOpen, setClientsOpen] = useState(false);

    const navRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    // Auto-close everything on route change
    useEffect(() => {
        setIsNavOpen(false);
        setServicesOpen(false);
        setLinksOpen(false);
        setHomeownersOpen(false);
        setClientsOpen(false);
    }, [location]);

    // Close on click-outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                navRef.current &&
                !navRef.current.contains(event.target as Node)
            ) {
                setServicesOpen(false);
                setLinksOpen(false);
                setHomeownersOpen(false);
                setClientsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const isMobile = () => window.innerWidth <= 990;

    const handleMouseEnter = (setter: (val: boolean) => void) => {
        if (!isMobile()) setter(true);
    };

    const handleMouseLeave = (setter: (val: boolean) => void) => {
        if (!isMobile()) setter(false);
    };

    const handleTriggerClick = (
        e: React.MouseEvent,
        setter: (val: boolean) => void,
        currentState: boolean,
    ) => {
        e.preventDefault();
        e.stopPropagation(); // Prevents child click from closing parent
        setter(!currentState);
    };

    return (
        <nav className="header" ref={navRef}>
            <div className="hamburger" onClick={() => setIsNavOpen(!isNavOpen)}>
                <div className={`bar1 ${isNavOpen ? "open" : ""}`}></div>
                <div className={`bar2 ${isNavOpen ? "open" : ""}`}></div>
                <div className={`bar3 ${isNavOpen ? "open" : ""}`}></div>
            </div>

            <div className={`nav-links ${isNavOpen ? "active" : ""}`}>
                <NavLink
                    to="/"
                    end
                    className={({ isActive }) => (isActive ? "active" : "")}
                >
                    Home
                </NavLink>

                <div
                    className="dropdown-container"
                    onMouseEnter={() => handleMouseEnter(setServicesOpen)}
                    onMouseLeave={() => handleMouseLeave(setServicesOpen)}
                >
                    <div
                        className={`dropdown-trigger ${isServicesOpen ? "open" : ""}`}
                        onClick={(e) =>
                            handleTriggerClick(
                                e,
                                setServicesOpen,
                                isServicesOpen,
                            )
                        }
                    >
                        Services <span className="arrow">▾</span>
                    </div>
                    {isServicesOpen && (
                        <div className="dropdown-content">
                            <NavLink to="/services/community-management">
                                Community Management
                            </NavLink>
                            <NavLink to="/services/financial-services">
                                Financial Services
                            </NavLink>
                            <NavLink to="/services/real-estate-services">
                                Real Estate Services
                            </NavLink>
                        </div>
                    )}
                </div>

                <NavLink to="/about-us">About Us</NavLink>
                <NavLink to="/request-a-proposal">Request A Proposal</NavLink>
                <NavLink to="/rental">Rental</NavLink>
                <NavLink to="/contact">Contact Us</NavLink>

                <div
                    className="dropdown-container"
                    onMouseEnter={() => handleMouseEnter(setLinksOpen)}
                    onMouseLeave={() => handleMouseLeave(setLinksOpen)}
                >
                    <div
                        className={`dropdown-trigger ${isLinksOpen ? "open" : ""}`}
                        onClick={(e) =>
                            handleTriggerClick(e, setLinksOpen, isLinksOpen)
                        }
                    >
                        Links <span className="arrow">▾</span>
                    </div>

                    {isLinksOpen && (
                        <div className="dropdown-content">
                            <div
                                className="sub-dropdown-container"
                                onMouseEnter={() =>
                                    handleMouseEnter(setHomeownersOpen)
                                }
                                onMouseLeave={() =>
                                    handleMouseLeave(setHomeownersOpen)
                                }
                            >
                                <div
                                    className="dropdown-trigger sub"
                                    onClick={(e) =>
                                        handleTriggerClick(
                                            e,
                                            setHomeownersOpen,
                                            isHomeownersOpen,
                                        )
                                    }
                                >
                                    Homeowners <span className="arrow">▾</span>
                                </div>
                                {isHomeownersOpen && (
                                    <div className="dropdown-content nested left-flyout">
                                        <a
                                            href="https://aepm.cincwebaxis.com/account/loginmodernthemes"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Manage Account
                                        </a>
                                        <a
                                            href="https://aepm.cincwebaxis.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Make Payment
                                        </a>
                                    </div>
                                )}
                            </div>

                            <div
                                className="sub-dropdown-container"
                                onMouseEnter={() =>
                                    handleMouseEnter(setClientsOpen)
                                }
                                onMouseLeave={() =>
                                    handleMouseLeave(setClientsOpen)
                                }
                            >
                                <div
                                    className="dropdown-trigger sub"
                                    onClick={(e) =>
                                        handleTriggerClick(
                                            e,
                                            setClientsOpen,
                                            isClientsOpen,
                                        )
                                    }
                                >
                                    Clients <span className="arrow">▾</span>
                                </div>
                                {isClientsOpen && (
                                    <div className="dropdown-content nested left-flyout">
                                        <a
                                            href="https://www.homewisedocs.com/nxlapp/appres/index.jsp"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Closing Request
                                        </a>
                                        <a
                                            href="https://www.homewisedocs.com/nxlapp/appres/index.xhtml"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Docs
                                        </a>
                                        <NavLink to="/home-wise-docs">
                                            Why Homewise Docs?
                                        </NavLink>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Header;
